import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ShoppingBag, CheckCheck, Package, PackageCheck, Truck, Home, XCircle, MapPin, ArrowRight, RefreshCw,
} from 'lucide-react';
import { api } from '@/lib/api';
import { proxyImg } from '@/lib/imageProxy';
import NPSPrompt from '@/components/NPSPrompt';
import { toast } from 'sonner';

const ICONS = { ShoppingBag, CheckCheck, Package, PackageCheck, Truck, Home, XCircle };

export default function OrderTracking() {
    const { orderId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => api.get(`/orders/${orderId}/tracking`)
        .then(({ data }) => setData(data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));

    useEffect(() => { load(); const t = setInterval(load, 25000); return () => clearInterval(t); }, [orderId]);

    if (loading) return <div className="min-h-[60vh] flex items-center justify-center">Loading…</div>;
    if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-text-secondary">Order not found.</div>;

    const { order, timeline, courier, current } = data;
    const isOOD = current === 'out_for_delivery';
    const completedSteps = timeline.filter((t) => t.state === 'done').length;
    const totalSteps = timeline.length;
    const progress = (completedSteps / totalSteps) * 100;

    return (
        <div data-testid="order-tracking-page" className="bg-cream min-h-screen">
            {/* Header strip */}
            <section className="bg-inorbit-blue-deeper text-white pt-28 pb-10" data-testid="track-header">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <Link to="/profile" className="text-xs uppercase tracking-widest text-inorbit-yellow hover:underline">← Back to my orders</Link>
                    <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
                        <div>
                            <div className="overline text-inorbit-yellow mb-2">Order #{order.order_number}</div>
                            <h1 className="font-serif text-4xl md:text-5xl leading-tight" style={{ color: 'white' }}>Tracking your order</h1>
                            <div className="mt-2 text-white/75 text-sm">From {order.brand_name} · Placed {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                        </div>
                        <button onClick={load} className="btn-yellow" data-testid="track-refresh-btn">
                            <RefreshCw size={14} /> Refresh
                        </button>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-8 h-1 bg-white/10 rounded-full overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.7 }} className="h-full bg-gradient-to-r from-inorbit-yellow to-inorbit-orange" />
                    </div>
                </div>
            </section>

            {/* Live courier strip — out for delivery only */}
            {isOOD && (
                <section className="bg-inorbit-orange text-white" data-testid="courier-strip">
                    <div className="max-w-5xl mx-auto px-6 md:px-12 py-5 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-4">
                            <Truck size={26} strokeWidth={1.8} className="shrink-0" />
                            <div>
                                <div className="text-[10px] uppercase tracking-widest opacity-80">{courier.partner} · Waybill {courier.waybill}</div>
                                <div className="font-serif text-2xl leading-tight">Arriving in {courier.eta_minutes} minutes</div>
                            </div>
                        </div>
                        <a href={courier.tracking_url} target="_blank" rel="noreferrer" className="text-xs uppercase tracking-widest font-semibold underline">Track on {courier.partner}</a>
                    </div>
                </section>
            )}

            <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 grid lg:grid-cols-3 gap-8">
                {/* TIMELINE */}
                <div className="lg:col-span-2">
                    <h2 className="font-serif text-2xl text-inorbit-blue mb-8">Status timeline</h2>
                    <ol className="relative border-l-2 border-border-subtle ml-3" data-testid="track-timeline">
                        {timeline.map((step, i) => {
                            const Icon = ICONS[step.icon] || ShoppingBag;
                            const cls = step.state === 'done'
                                ? 'bg-inorbit-orange text-white border-inorbit-orange'
                                : step.state === 'current'
                                    ? 'bg-inorbit-yellow text-inorbit-blue border-inorbit-yellow ring-4 ring-inorbit-yellow/30 animate-pulse'
                                    : 'bg-white text-text-tertiary border-border-subtle';
                            return (
                                <li key={i} className="ml-8 mb-8" data-testid={`step-${step.status}`}>
                                    <span className={`absolute -left-[18px] w-9 h-9 rounded-full border-2 flex items-center justify-center ${cls}`}>
                                        <Icon size={15} strokeWidth={2} />
                                    </span>
                                    <div className="flex items-baseline justify-between gap-2">
                                        <div className={`font-serif text-xl ${step.state === 'pending' ? 'text-text-tertiary' : 'text-inorbit-blue'}`}>{step.label}</div>
                                        {step.at && <div className="text-[10px] uppercase tracking-widest text-text-tertiary">{new Date(step.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</div>}
                                    </div>
                                    <div className={`text-sm leading-relaxed mt-1 ${step.state === 'pending' ? 'text-text-tertiary' : 'text-text-secondary'}`}>{step.blurb}</div>
                                    {step.note && <div className="mt-2 text-xs text-inorbit-orange italic">"{step.note}"</div>}
                                </li>
                            );
                        })}
                    </ol>
                </div>

                {/* RIGHT — Order summary */}
                <div data-testid="order-summary">
                    <div className="card-luxury p-6 mb-5">
                        <div className="overline mb-3">Items</div>
                        <div className="space-y-3 mb-5">
                            {(order.items || []).map((it, i) => (
                                <div key={i} className="flex items-center gap-3">
                                    <div className="w-14 h-14 rounded bg-cream overflow-hidden shrink-0">
                                        {it.image && <img src={proxyImg(it.image)} alt={it.name} className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-inorbit-blue line-clamp-1">{it.name}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-text-tertiary">Qty {it.quantity} · ₹{Number(it.price).toLocaleString('en-IN')}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="border-t border-border-subtle pt-4 space-y-1.5 text-sm">
                            <div className="flex justify-between text-text-secondary"><span>Subtotal</span><span>₹{Number(order.subtotal || 0).toLocaleString('en-IN')}</span></div>
                            {order.discount > 0 && <div className="flex justify-between text-inorbit-orange"><span>Discount</span><span>− ₹{Number(order.discount).toLocaleString('en-IN')}</span></div>}
                            {order.tax > 0 && <div className="flex justify-between text-text-secondary"><span>GST</span><span>₹{Number(order.tax).toLocaleString('en-IN')}</span></div>}
                            <div className="flex justify-between font-serif text-xl text-inorbit-blue pt-2 border-t border-border-subtle"><span>Total</span><span>₹{Number(order.total || 0).toLocaleString('en-IN')}</span></div>
                        </div>
                    </div>
                    <div className="card-luxury p-6">
                        <div className="overline mb-3 flex items-center gap-2"><MapPin size={12} /> Delivery</div>
                        <div className="text-sm text-text-secondary leading-relaxed">{order.delivery_address || 'Address on file'}</div>
                        <div className="mt-4 text-xs text-text-tertiary">Payment · {order.payment_status?.replace('_', ' ')}</div>
                        <Link to="/shop" className="btn-link mt-5 inline-flex">Shop again <ArrowRight size={14} /></Link>
                    </div>

                    {current === 'delivered' && (
                        <ReturnRequestCard order={order} />
                    )}
                </div>
            </div>

            {current === 'delivered' && (
                <div className="max-w-5xl mx-auto px-6 md:px-12 pb-16">
                    <NPSPrompt category="order" targetId={order.id} title={`How was your order from ${order.brand_name}?`} />
                </div>
            )}
        </div>
    );
}


function ReturnRequestCard({ order }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    const REASONS = ['Wrong size', 'Damaged in transit', 'Different from description', 'Quality not as expected', 'Changed my mind', 'Other'];

    const submit = async () => {
        if (!reason) { toast.error('Please pick a reason'); return; }
        setBusy(true);
        try {
            const items = (order.items || []).map((it) => ({
                product_id: it.product_id, name: it.name, quantity: it.quantity,
                refund_amount: Number(it.price) * Number(it.quantity),
            }));
            const { data } = await api.post(`/orders/${order.id}/return`, { items, reason, notes });
            setDone(data);
            toast.success(`Return request submitted · ${data.rma_number}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not submit return');
        } finally { setBusy(false); }
    };

    if (done) {
        return (
            <div className="card-luxury p-6 border-2 border-emerald-500/30 bg-emerald-50" data-testid="return-success">
                <div className="overline text-emerald-700">Return requested</div>
                <div className="font-serif text-xl text-inorbit-blue mt-1">{done.rma_number}</div>
                <p className="text-sm text-text-secondary mt-2">Refund of ₹{Number(done.refund_amount).toLocaleString('en-IN')} initiated. Track status on <Link to="/account/returns" className="text-inorbit-orange underline">My Returns</Link>.</p>
            </div>
        );
    }

    return (
        <div className="card-luxury p-6" data-testid="return-request-card">
            <div className="overline mb-1">Not satisfied?</div>
            <div className="font-serif text-xl text-inorbit-blue mb-3">Request a return</div>
            {!open ? (
                <button onClick={() => setOpen(true)} className="btn-outline border-inorbit-blue text-inorbit-blue hover:bg-inorbit-blue hover:text-white w-full justify-center" data-testid="open-return-btn">
                    Start return
                </button>
            ) : (
                <div className="space-y-3">
                    <div>
                        <label className="overline mb-1.5 block text-[10px]">Reason</label>
                        <select value={reason} onChange={(e) => setReason(e.target.value)} className="input-box w-full" data-testid="return-reason">
                            <option value="">Choose…</option>
                            {REASONS.map((r) => <option key={r}>{r}</option>)}
                        </select>
                    </div>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else? (optional)" rows={2} className="input-box w-full" data-testid="return-notes" />
                    <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="submit-return-btn">
                        {busy ? 'Submitting…' : 'Submit return'}
                    </button>
                </div>
            )}
        </div>
    );
}
